import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { Column, Section } from '@ydistri/ds';
import { useTemplateOrCalculation } from '../../../../../../../hooks/useTemplateOrCalculation';
import { useGetCalculationQuery } from '../../../../../apiCalculationDetail';
import {
  useGetCalculationSkusQuery,
  useGetMonthlyForecastQuery,
  useGetMonthlySalesQuery,
  useGetSkuAttributesQuery,
} from '../../../../../../Calculations/apiCalculationsSkus';
import SkuDetailActions from './SkuDetailActions';
import SkuDataComponent from './SkuAttributes/SkuDataComponent';
import {
  ForecastsObject,
  MinMaxForecast,
  SkuRedistributionPaneType,
  SkuRedistributionSubset,
} from '../../../../redistributionTypes';
import CalculationRedistributionSectionHeader from '../CalculationRedistributionSectionHeader';
import SkuSaleChart from '../SkuSaleChart/SkuSaleChart';
import {
  AttributeDefinitionType,
  CalculationMode,
  ConfigurationFieldType,
  OverviewValueType,
  SaleMonthlyOverviewResponse,
  TransactionOverviewType,
} from '@ydistri/api-sdk';
import { joinSalesAndForecast } from '../SkuSaleChart/SkuSaleChartLib';
import { parseSku } from '../../../../redistributionLib';
import SkuInfoSource from './SkuAttributes/SkuInfoSource';
import { useSelectedTemplateId } from '../../../../../../../hooks/useSelectedTemplateId';
import { useGetTemplateQuery } from '../../../../../../Configuration/apiTemplate';
import SkuInfoTarget from './SkuAttributes/SkuInfoTarget';
import { useWindowDimension } from '../../../../../../../hooks/useWindowDimension';
import { createDebugLog } from '@ydistri/utils';
import { generatePath } from 'react-router';
import { ROUTES } from '../../../../../../../components/menu/getMenuLeftItems';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../../../store';
import { Alert } from 'antd';
import {
  SUBCALCULATION_SOURCE_SELECTOR_TABLE_ID,
  SUBCALCULATION_TARGET_SELECTOR_TABLE_ID,
} from '../SubordinateCalculationSelector/subordinateCalculationSelectorLib';
import SubordinateCalculationSelector from '../SubordinateCalculationSelector/SubordinateCalculationSelector';
import { useStableComputedValue } from '../../../../../../../hooks/useStableComputedValue';
import { computeRemSize } from '@ydistri/theme';
import { useGetSkuDescriptionQuery } from '../../../../../../Detail/apiDetail';
import { useGetSkuClassesQuery } from '../../../../../../../apis/apiLists';
import { NO_VALUE } from '../../../../../../../lib/utils/utilsTypes';
import { useStableParams } from '../../../../../../../routes/useStableParams';

interface SkuDetailSectionProps {
  redistributionData: SkuRedistributionSubset;
  sourceOrTarget: SkuRedistributionPaneType;
  showExecutionResult: boolean;
  executionResultLoading: boolean;
  onShowSkuInfoChanged?: (showSkuInfo: boolean) => void;
}

const debugLog = createDebugLog('SkuDetailSection');

const opacityStyle = { opacity: 0.3 };
const EMPTY_ARRAY: unknown[] = [] as const;

const SectionBody = styled.div`
  min-height: ${computeRemSize(300)};
  // max-height: calc(100vh - ${computeRemSize(370)});
`;

function getForecastForTypeFromMonthlyTransactions(
  monthlySales: SaleMonthlyOverviewResponse[],
  forecastType: TransactionOverviewType,
): number[] {
  const forecastsForType: number[] = [];
  let forecastCumulative = 0;

  monthlySales.forEach(sale => {
    const foundForecast = sale.overviews.filter(transactionValue => {
      return (
        transactionValue.overviewValueType === OverviewValueType.Quantity &&
        transactionValue.transactionOverviewType === forecastType
      );
    });
    if (foundForecast.length > 0) {
      forecastCumulative += foundForecast[0].value;
      forecastsForType.push(forecastCumulative);
    }
  });

  return forecastsForType;
}

const SkuDetailSection: React.FC<SkuDetailSectionProps> = ({
  redistributionData,
  sourceOrTarget,
  showExecutionResult,
  executionResultLoading,
  onShowSkuInfoChanged,
}) => {
  const calculationData = useTemplateOrCalculation();
  const { data: calculation } = useGetCalculationQuery(calculationData.id);
  const applicationDate = useMemo(
    () => new Date(calculation?.applicationDate ?? 0),
    [calculation?.applicationDate],
  );

  const selectedTemplateId = useSelectedTemplateId();
  const { data: template } = useGetTemplateQuery(selectedTemplateId);

  const [showActualData, setShowActualData] = useState<boolean>(false);
  const [detailCumulativeForecast, setDetailCumulativeForecast] = useState<boolean>(false);
  const [showSkuInfo, setShowSkuInfo] = useState<boolean>(false);
  const selectedCalculationId = useSelector(
    (state: ReduxState) =>
      state.calculations.selectedRowKeys[
        sourceOrTarget === 'Source'
          ? SUBCALCULATION_SOURCE_SELECTOR_TABLE_ID
          : SUBCALCULATION_TARGET_SELECTOR_TABLE_ID
      ]?.arr[0],
  );

  const windowDimension = useWindowDimension();

  const { projectShortName } = useStableParams();
  const selectedCategorySlug = useSelector(
    (state: ReduxState) => state.categoryTreeReducer.currentlySelectedCategorySlug,
  );

  /**
   * Make chart's height dynamic based on the window size.
   * Window is bigger on higher resolutions so the chart should have reasonable
   * height on all resolutions.
   * Minimum height is 290px.
   */
  const chartHeight = useMemo(() => {
    // 3.6 is the ratio of the chart height to the window height that is just about right
    const newHeight = Math.floor(windowDimension.height / 3.6);
    debugLog('new chart height: %d, window size is', newHeight, windowDimension);
    return Math.max(newHeight, 290);
  }, [windowDimension]);

  const { skuId } = redistributionData;

  const { data: skuConfiguration, isFetching: skuConfigurationFetching } =
    useGetCalculationSkusQuery({
      skuId: skuId,
      calculationId: calculationData.id,
    });

  const mergedSelected = skuConfiguration?.calculation.id === selectedCalculationId;

  const selectedSkuConfiguration = useMemo(() => {
    if (!skuConfigurationFetching && skuConfiguration && selectedCalculationId) {
      if (mergedSelected) return skuConfiguration;
      if (!skuConfiguration.subordinateCalculationSkus) return undefined;
      const submergedSelected = skuConfiguration.subordinateCalculationSkus.find(
        c => c.calculation.id === selectedCalculationId,
      );
      if (submergedSelected) {
        return {
          ...submergedSelected,
          // if we keep in/outgoing quantity from submerged, it will mess up computation of final supply,
          // that's why we replace it with submerged (state before the first submerged)
          outgoingQuantity: skuConfiguration.outgoingQuantity,
          incomingQuantity: skuConfiguration.incomingQuantity,
          // submerged don't have skuClass for some reason, but it has to be the same as from the main merged calculation
          skuClass: skuConfiguration.skuClass,
        };
      }
      return undefined;
    }
    return skuConfiguration;
  }, [mergedSelected, selectedCalculationId, skuConfiguration, skuConfigurationFetching]);

  const { data: skuAttributes, isFetching: skuAttributesFetching } = useGetSkuAttributesQuery({
    skuId,
    calculationId: calculationData.id,
    retrieveActualData: showActualData,
  });

  // sku attributes are either from calculation or actual data
  // they contain only Ids, so we need to fetch sku description separately
  const skuDescriptionInfo = skuAttributes?.find(
    attr => attr.type === AttributeDefinitionType.SkuDescriptionId,
  );

  const { data: skuDescription, isFetching: skuDescriptionFetching } = useGetSkuDescriptionQuery(
    skuDescriptionInfo && skuDescriptionInfo.value.length > 0
      ? parseInt(skuDescriptionInfo.value)
      : NO_VALUE,
  );

  // sku attributes are either from calculation or actual data
  // they contain only Ids, so we need to fetch sku class separately
  const skuClassInfo = skuAttributes?.find(
    attr => attr.type === AttributeDefinitionType.SkuClassId,
  );
  const { data: skuClasses, isFetching: skuClassFetching } = useGetSkuClassesQuery({
    skuClassId:
      skuClassInfo && skuClassInfo.value.length > 0 ? parseInt(skuClassInfo.value) : NO_VALUE,
  });

  const { data: monthlySales, isFetching: monthlySalesFetching } = useGetMonthlySalesQuery({
    skuId,
    calculationId: calculationData.id,
    monthsCount: 24,
  });

  const { data: monthlyForecast, isFetching: monthlyForecastFetching } = useGetMonthlyForecastQuery(
    {
      skuId,
      calculationId: calculationData.id,
    },
  );

  const isFetching =
    skuAttributesFetching ||
    skuDescriptionFetching ||
    skuConfigurationFetching ||
    monthlySalesFetching ||
    monthlyForecastFetching ||
    skuClassFetching;

  const skuData = useMemo(() => {
    if (isFetching) return;
    if (selectedSkuConfiguration && skuAttributes && skuAttributes.length > 0) {
      return {
        ...parseSku(selectedSkuConfiguration, skuAttributes, mergedSelected),
        description: skuDescription,
        skuClass: skuClasses && skuClasses.length > 0 ? skuClasses[0] : undefined,
      };
    }
  }, [
    isFetching,
    selectedSkuConfiguration,
    skuAttributes,
    mergedSelected,
    skuDescription,
    skuClasses,
  ]);

  /**
   * Use date from calculation or template based on the showActualData flag
   */
  const dateToUse: Date = useMemo(() => {
    if (showActualData) {
      if (template) {
        return new Date(template.applicationDate);
      }
    } else {
      if (calculation) {
        return new Date(calculation.applicationDate);
      }
    }
    return new Date();
  }, [calculation, showActualData, template]);

  useEffect(() => {
    if (onShowSkuInfoChanged) {
      onShowSkuInfoChanged(showSkuInfo);
    }
  }, [onShowSkuInfoChanged, showSkuInfo]);

  const chartData: SaleMonthlyOverviewResponse[] = useStableComputedValue(
    () => {
      if (monthlySales && monthlyForecast && skuData) {
        const minConfidence = parseInt(
          skuData.config[ConfigurationFieldType.PtMinForecastConfidence] ?? '0',
        );
        const maxConfidence = parseInt(
          skuData.config[ConfigurationFieldType.PsMaxForecastConfidence] ?? '0',
        );

        if (minConfidence && maxConfidence) {
          return joinSalesAndForecast(monthlySales, monthlyForecast, minConfidence, maxConfidence);
        }

        return monthlySales;
      }
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- using stable array
      return EMPTY_ARRAY as SaleMonthlyOverviewResponse[];
    },
    isFetching,
    [monthlyForecast, monthlySales, skuData],
  );

  const forecasts: ForecastsObject = useMemo(
    () => ({
      min: getForecastForTypeFromMonthlyTransactions(
        chartData,
        TransactionOverviewType.ForecastMin,
      ),
      max: getForecastForTypeFromMonthlyTransactions(
        chartData,
        TransactionOverviewType.ForecastMax,
      ),
    }),
    [chartData],
  );

  const usedForecast: MinMaxForecast = useMemo(() => {
    let min = 0;
    let max = 0;

    if (skuData && forecasts.min.length > 0 && forecasts.max.length > 0) {
      const availableSupply = skuData.attributes.values.AvailableSupply
        ? parseFloat(skuData.attributes.values.AvailableSupply)
        : 0;
      const psForecastMonths = parseInt(skuData.config.PsMonthsOfSupplyToKeep ?? '0');
      const ptForecastMonths = parseInt(skuData.config.PtMonthsOfSupplyToGetByForecast ?? '0');

      const forecastMax = psForecastMonths > 0 ? forecasts.max[psForecastMonths - 1] : 0;
      const forecastMin = ptForecastMonths > 0 ? forecasts.min[ptForecastMonths - 1] : 0;

      if (availableSupply >= 0 && forecastMax >= 0 && forecastMin >= 0) {
        max = parseFloat(
          forecastMax.toFixed(availableSupply === Math.round(availableSupply) ? 0 : 2),
        );
        min = parseFloat(
          forecastMin.toFixed(availableSupply === Math.round(availableSupply) ? 0 : 2),
        );
      }
    }

    return {
      min,
      max,
    };
  }, [forecasts, skuData]);

  const toggleActualData = useCallback(() => {
    setShowActualData(prevValue => !prevValue);
  }, []);

  const toggleCumulativeForecast = useCallback(() => {
    setDetailCumulativeForecast(prevValue => !prevValue);
  }, []);

  const toggleShowSkuInfo = useCallback(() => {
    setShowSkuInfo(prevValue => !prevValue);
  }, []);

  const manualCalculation = calculation?.mode === CalculationMode.Manual;
  const mergedCalculation = calculation?.mode === CalculationMode.Merged;

  const headerActions = useMemo(() => {
    return (
      <SkuDetailActions
        onShowActualData={toggleActualData}
        showActualData={showActualData}
        onToggleCumulativeForecast={toggleCumulativeForecast}
        cumulativeForecast={detailCumulativeForecast}
        onToggleShowSkuInfo={toggleShowSkuInfo}
        showSkuInfo={showSkuInfo}
        manualCalculation={manualCalculation}
        skuId={skuId}
      />
    );
  }, [
    toggleActualData,
    showActualData,
    toggleCumulativeForecast,
    detailCumulativeForecast,
    toggleShowSkuInfo,
    showSkuInfo,
    manualCalculation,
    skuId,
  ]);

  const sectionHeader = useMemo(() => {
    const { store } = redistributionData;

    const skuDetailPath = generatePath(ROUTES.detail, {
      projectShortName: projectShortName ?? '',
      slug: selectedCategorySlug,
    });

    const skuDetailUrl = `${skuDetailPath}?sku=${skuId}`;

    return (
      <CalculationRedistributionSectionHeader
        type="store"
        entity={store}
        skuId={skuId}
        link={skuDetailUrl}
        storeDecileOverride={
          skuData?.attributes.values[AttributeDefinitionType.StoreDecile] ?? null
        }
        brandQuintile={redistributionData.brandQuintile}
      />
    );
  }, [projectShortName, redistributionData, selectedCategorySlug, skuId, skuData]);

  return (
    <Section
      header={sectionHeader}
      $scrollableContent={true}
      headerActions={headerActions}
      $ratio={1}
      $basis={1}
      $shrink={0}
      $backgroundColor={showActualData ? '#fffcf8' : undefined}
    >
      <SectionBody data-type="SkuDetailSectionBody">
        <Column
          $flexWrap="nowrap"
          $gap={computeRemSize(16)}
          style={isFetching ? opacityStyle : undefined}
        >
          {!skuData && !isFetching ? (
            <Alert message={`No data for this ${sourceOrTarget} SKU`} type="warning" showIcon />
          ) : (
            <>
              {showSkuInfo && calculation?.mode === CalculationMode.Merged && (
                <SubordinateCalculationSelector skuId={skuId} sourceOrTarget={sourceOrTarget} />
              )}
              <SkuDataComponent
                infoType={sourceOrTarget}
                skuData={skuData}
                redistributionData={redistributionData}
                showingActualData={showActualData}
                manualCalculation={manualCalculation}
                showExecutionResult={showExecutionResult}
                executionResultLoading={executionResultLoading}
                hideTooltip={mergedCalculation && !mergedSelected}
              />
              {showSkuInfo && sourceOrTarget === 'Source' && (
                <SkuInfoSource
                  skuData={skuData}
                  applicationDate={dateToUse}
                  usedForecast={usedForecast}
                  showActualData={showActualData}
                  hideSkuInfo={calculation?.mode === CalculationMode.Manual}
                />
              )}
              {showSkuInfo && sourceOrTarget === 'Target' && (
                <SkuInfoTarget
                  skuData={skuData}
                  applicationDate={dateToUse}
                  usedForecast={usedForecast}
                  showActualData={showActualData}
                  hideSkuInfo={calculation?.mode === CalculationMode.Manual}
                />
              )}
              {monthlySales && calculation && (
                <SkuSaleChart
                  applicationDate={applicationDate}
                  rawData={chartData}
                  cumulativeMode={detailCumulativeForecast}
                  height={chartHeight}
                  vat={parseFloat(skuData?.attributes.values[AttributeDefinitionType.Vat] ?? '0')}
                />
              )}
            </>
          )}
        </Column>
      </SectionBody>
    </Section>
  );
};

const MemoSkuDetailSection = React.memo(SkuDetailSection);
MemoSkuDetailSection.displayName = 'SkuDetailSection';

export default MemoSkuDetailSection;
